import { isEmpty } from 'ramda';
import { createAction } from 'redux-actions';

import { REQUEST_SIZE } from 'constants/general';
import fetch from 'features/common/fetch';
import { updateFetchRequestMetadata } from 'features/common/fetch/actions';
import { subheaders } from 'features/common/Subheader/constants';
import { getVisibleStateById } from 'features/common/Subheader/selectors';
import { setVisibleState } from 'features/common/Subheader/slice';
// eslint-disable-next-line import/no-cycle
import typeTasks from 'features/other/typeTasks/index';
import { QUICK_FILTER_STATES, relations } from 'features/views/taskOverview/constants';
import { TASK_OVERVIEW_FORM_NAME } from 'features/views/taskOverview/form';
import { getQuickFilterState, getSubheaderState } from 'features/views/taskOverview/selectors';
import { renameKeysByRelations, requestFactory } from 'features/views/taskOverview/utils';
import userContext from 'features/views/userContext';
import { trimFormData } from 'utils';

import * as types from './actionTypes';
import { FILTER_TYPE } from './constants';
// eslint-disable-next-line import/no-cycle
import { getPersistTypeTasksVisibleState } from './selectors';
import { fetchStorePathForTaskTypeId, fetchStorePathUniqueRequestSuffixForTaskTypeId } from './utils';

export const saveTaskDetail = createAction(types.SAVE_TASK_DETAIL);
export const reset = createAction(types.RESET);
export const setSort = createAction(types.SET_SORT, (sort, taskTypeId) => ({ sort, taskTypeId }));
export const setPersistTypeTasksVisibleState = createAction(
	types.SET_PERSIST_TYPE_TASKS_VISIBLE_STATE,
	(persistTypeTasksVisibleState) => ({
		persistTypeTasksVisibleState,
	}),
);

export const setPageIds = createAction(types.SET_PAGE_IDS, (data, taskTypeId, filterOpen) => ({
	data,
	taskTypeId,
	filterType: filterOpen ? FILTER_TYPE.custom : FILTER_TYPE.quick,
}));

export const appendPageIds = createAction(types.APPEND_PAGE_IDS, (data, taskTypeId, filterOpen) => ({
	data,
	taskTypeId,
	filterType: filterOpen ? FILTER_TYPE.custom : FILTER_TYPE.quick,
}));

export const setTaskView = createAction(types.SET_TASK_VIEW, (taskId, viewed) => ({
	taskId,
	viewed,
}));

export const selectTask = createAction(types.SELECT_TASK, (taskTypeId, taskId) => ({ taskTypeId, taskId }));

// if is called without "taskTypeId" clear all selected tasks
export const clearSelectTasks = createAction(types.CLEAR_SELECT_TASKS);

export const selectAllTasks = createAction(types.SELECT_ALL_TASK, (taskTypeId, areSelected) => ({
	taskTypeId,
	areSelected,
}));

// if is called without "taskTypeId" clear all selected tasks state
export const clearAllSelected = createAction(types.CLEAR_ALL_SELECT);

const createPaginationAction = ({ taskTypeId, page, state, queryParamsOverride, loadMore = false }) => {
	const contextUser = userContext.selectors.getContextUser(state);
	const uniqueReqID = fetchStorePathUniqueRequestSuffixForTaskTypeId(taskTypeId, state);
	const fetchPath = fetchStorePathForTaskTypeId(taskTypeId, state);

	const { queryParams, body } = fetch.selectors.getLastRequest(fetchPath)(state);

	return fetch.actions.post(fetch.constants.urls.taskList, {
		uniqueReqID,
		queryParams: {
			...queryParams,
			page: loadMore ? queryParams?.page + 1 : page,
			size: REQUEST_SIZE,
			...queryParamsOverride,
		},
		body,
		additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
	});
};

export const loadMore = (taskTypeId) => (dispatch, getState) => {
	const state = getState();
	const filterOpen = getSubheaderState(state);
	const action = createPaginationAction({ taskTypeId, state, loadMore: true });

	dispatch(action).then(({ body }) => {
		const paginationIds = body.data.map((task) => task.id);
		dispatch(saveTaskDetail(body.data));
		dispatch(appendPageIds(paginationIds, taskTypeId, filterOpen));
	});
};

export const paginationClick = (taskTypeId, page) => (dispatch, getState) => {
	const state = getState();
	const filterOpen = getSubheaderState(state);
	const action = createPaginationAction({ taskTypeId, page, state });

	dispatch(action).then(({ body }) => {
		const paginationIds = body.data.map((task) => task.id);
		dispatch(saveTaskDetail(body.data));
		dispatch(setPageIds(paginationIds, taskTypeId, filterOpen));
	});
};

export const reloadCurrentPage = (taskTypeId) => (dispatch, getState) => {
	const state = getState();
	const fetchStorePath = fetchStorePathForTaskTypeId(taskTypeId, state);
	const metaData = fetch.selectors.getMetaData(fetchStorePath)(state);

	// do not reload page when it wasn't loaded
	if (isEmpty(metaData)) {
		return;
	}

	const numberOfLoadedTasks = typeTasks.selectors.getTypeTasksById(taskTypeId)(state).length;
	const numberOfTasksToLoad = (metaData.page + 1) * REQUEST_SIZE;
	const numberOfLoadedPages = Math.ceil(numberOfLoadedTasks / REQUEST_SIZE);

	const action = createPaginationAction({
		taskTypeId,
		page: 0,
		state,
		queryParamsOverride: { size: numberOfTasksToLoad },
	});

	dispatch(action).then(({ body }) => {
		const data = body.data.slice((metaData.page + 1 - numberOfLoadedPages) * REQUEST_SIZE);
		const pageMetaData = body.pageMetaData;
		const paginationIds = data.map((task) => task.id);
		const filterOpen = getSubheaderState(state);

		dispatch(saveTaskDetail(data));
		dispatch(setPageIds(paginationIds, taskTypeId, filterOpen));
		dispatch(
			updateFetchRequestMetadata(fetchStorePath)({
				page: metaData.page,
				size: REQUEST_SIZE,
				totalRows: pageMetaData.totalRows,
			}),
		);

		// load last page when we reach the end of list during reload
		if (data.length === 0 && metaData.page > 0) {
			dispatch(paginationClick(taskTypeId, Math.ceil(pageMetaData.totalRows / REQUEST_SIZE) - 1));
		}
	});
};

export const fetchTypeTasks =
	(taskTypeId, page = 0) =>
	async (dispatch, getState, { getFormData }) => {
		const state = getState();
		const contextUser = userContext.selectors.getContextUser(state);
		const data = await getFormData(TASK_OVERVIEW_FORM_NAME);
		const formData = trimFormData(data);
		const customFilterOpened = getVisibleStateById(state, subheaders.tasksOverview);
		const quickFilterState = getQuickFilterState(state);
		let body = {
			filter: [],
		};

		if (customFilterOpened) {
			const formDataRenamedFileds = renameKeysByRelations(relations, formData);
			body = requestFactory(formDataRenamedFileds, taskTypeId);
		} else {
			body.filter.push({
				operation: 'equal',
				property: 'taskTypeId',
				value: [`${taskTypeId}`],
			});
		}

		body.ignoreTasksLimit = true;
		const sort = 'createDate,desc';

		if (
			(quickFilterState === QUICK_FILTER_STATES.all ||
				quickFilterState === QUICK_FILTER_STATES.tasks ||
				quickFilterState === QUICK_FILTER_STATES.info) &&
			!customFilterOpened
		) {
			body.filter.push({
				operation: 'equal',
				property: 'statusId',
				value: ['1', '2'],
			});
		} else if (quickFilterState === QUICK_FILTER_STATES.processed && !customFilterOpened) {
			body.filter.push({
				operation: 'equal',
				property: 'statusId',
				value: ['3', '4'],
			});
		}

		const fetchAction = fetch.actions.post(fetch.constants.urls.taskList, {
			uniqueReqID: fetchStorePathUniqueRequestSuffixForTaskTypeId(taskTypeId, state),
			queryParams: { size: REQUEST_SIZE, page, sort },
			body,
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
		});

		dispatch(fetchAction).then(({ body }) => {
			const paginationIds = body.data.map((task) => task.id);
			dispatch(saveTaskDetail(body.data));
			dispatch(setPageIds(paginationIds, taskTypeId, customFilterOpened));
		});
	};

export const sortTypeTasks = (sort, typeTaskId) => (dispatch) => {
	dispatch(setSort(sort, typeTaskId));
	dispatch(loadSorted(sort, typeTaskId));
};

export const loadSorted = (sort, taskTypeId) => (dispatch, getState) => {
	const state = getState();
	const contextUser = userContext.selectors.getContextUser(state);
	const uniqueReqID = fetchStorePathUniqueRequestSuffixForTaskTypeId(taskTypeId, state);
	const fetchPath = fetchStorePathForTaskTypeId(taskTypeId, state);
	const filterOpen = getSubheaderState(state);
	const { queryParams, body } = fetch.selectors.getLastRequest(fetchPath)(getState());

	dispatch(
		fetch.actions.post(fetch.constants.urls.taskList, {
			uniqueReqID,
			queryParams: {
				...queryParams,
				sort,
			},
			body,
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
		}),
	).then(({ body }) => {
		const paginationIds = body.data.map((task) => task.id);
		dispatch(saveTaskDetail(body.data));
		dispatch(setPageIds(paginationIds, taskTypeId, filterOpen));
	});
};

export const possiblyCloseCollapseTypeTasks = (subheaderId) => (dispatch, getState) => {
	const persistTypeTasksVisibleState = getPersistTypeTasksVisibleState(getState());
	if (!persistTypeTasksVisibleState) {
		dispatch(setVisibleState({ id: subheaderId, visible: false }));
	}
};
